import { useTranslation } from 'react-i18next'
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import logoImg from '@/assets/images/logo/logo.png'
import type { RouteKey } from '@/i18n/routes'

interface NavItem {
  key: RouteKey
  labelKey: string
}

const navigationMain: NavItem[] = [
  { key: 'about', labelKey: 'nav.about' },
  { key: 'management', labelKey: 'nav.management' },
  { key: 'consulting', labelKey: 'nav.consulting' },
  { key: 'training', labelKey: 'nav.training' },
  { key: 'careers', labelKey: 'nav.careers' },
  { key: 'locations', labelKey: 'nav.locations' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

const navigationLegal: NavItem[] = [
  { key: 'legalNotes', labelKey: 'footer.legalNotes' },
  { key: 'privacy', labelKey: 'footer.privacy' },
  { key: 'cookiePolicy', labelKey: 'footer.cookiePolicy' },
]

const socialLinks = [
  { name: 'Facebook', href: 'https://facebook.com', icon: Facebook },
  { name: 'Instagram', href: 'https://instagram.com', icon: Instagram },
  { name: 'LinkedIn', href: 'https://linkedin.com', icon: Linkedin },
]

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom py-10 sm:py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            <LocalizedLink to="home" className="inline-block mb-3 sm:mb-4">
              <img
                src={logoImg}
                alt="BF Wellness"
                className="h-12 sm:h-16 w-auto brightness-0 invert"
              />
            </LocalizedLink>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
              {t('footer.tagline')}
            </p>
            <p className="text-gray-400 text-xs sm:text-sm">
              <strong className="text-white">{t('footer.managementConsulting')}</strong>
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              {navigationMain.slice(0, 4).map((item) => (
                <li key={item.key}>
                  <LocalizedLink
                    to={item.key}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-xs sm:text-sm"
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4 invisible sm:visible">&nbsp;</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              {navigationMain.slice(4).map((item) => (
                <li key={item.key}>
                  <LocalizedLink
                    to={item.key}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-xs sm:text-sm"
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">{t('footer.contacts')}</h3>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start gap-2 sm:gap-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-accent-blue flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-xs sm:text-sm">
                  {t('contact.address')}<br />
                  {t('contact.city')}
                </span>
              </li>
              <li className="flex items-center gap-2 sm:gap-3">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-accent-blue flex-shrink-0" />
                <a
                  href={`mailto:${t('contact.email')}`}
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-xs sm:text-sm break-all"
                >
                  {t('contact.email')}
                </a>
              </li>
              <li className="flex items-center gap-2 sm:gap-3">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-accent-blue flex-shrink-0" />
                <a
                  href="tel:+390600000000"
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-xs sm:text-sm"
                >
                  {t('contact.phone')}
                </a>
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  aria-label={item.name}
                >
                  <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 sm:mt-10 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left">
              &copy; {new Date().getFullYear()} BF Wellness. {t('footer.allRightsReserved')} {t('footer.vatNumber')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
              {navigationLegal.map((item) => (
                <LocalizedLink
                  key={item.key}
                  to={item.key}
                  className="text-gray-500 hover:text-white transition-colors duration-200 text-xs sm:text-sm"
                >
                  {t(item.labelKey)}
                </LocalizedLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
